import { Link } from "wouter";
import ArabicToggle from "@/components/ArabicToggle";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SEOHead from "@/components/SEOHead";
import { LOCAL_FINAL_SPECS } from "@/data/localFinalSpec";
import { LOCAL_AEO_STRATEGY_SPECS } from "@/data/localAeoStrategySpec";
import { LocalAeoPlanFactsBlock } from "@/components/LocalAeoStrategyBlocks";
import RelatedToolsAndGuides from "@/components/RelatedToolsAndGuides";
import LocalIndustryGuideLinks from "@/components/LocalIndustryGuideLinks";
import { trackSectionCtaClick } from "@/lib/analytics";
import { ArrowRight, CheckCircle, MapPin, Zap, TrendingUp, Globe, BarChart2, Layout, Search, Users, Linkedin, Target } from "lucide-react";

import SchemaScript from "@/components/SchemaScript";
const LEGACY_FAQS = [
  {
    q: "What does the Full AI Growth Engine include for a local business in SW London or Surrey?",
    a: "The Full AI Growth Engine (£1,299/mo) is a complete done-for-you digital growth system. It includes a full website build of 10 or more pages with AI and local search optimisation, Agentic AI lead generation, LinkedIn management with 3 posts per week, PPC Performance Max campaigns, Google Ads with hyper-local targeting for your SW London or Surrey area, GA4 attribution, weekly content recommendations, and all Local Presence features. It is designed for businesses in Richmond, Putney, Wimbledon, Kingston, Guildford, and across SW London and Surrey that want a single agency managing their entire digital presence.",
  },
  {
    q: "What is Agentic AI lead generation and how does it work for a local business?",
    a: "Agentic AI lead generation uses AI systems to identify, qualify, and engage potential customers before they reach your website. For a gym in Wimbledon, this might mean identifying local residents who have recently searched for fitness options, engaging them with personalised content, and routing qualified leads to your booking system. For a restaurant in Richmond, it means targeting people who have visited competitor restaurants or searched for dining options in your area. The AI system runs continuously, learning from conversion data to improve targeting over time.",
  },
  {
    q: "How many LinkedIn posts per week are included and what type of content is produced?",
    a: "The Full AI Growth Engine includes 3 LinkedIn posts per week. For local businesses in SW London and Surrey, we produce a mix of community-focused content (local events, area highlights, customer stories), educational content (tips relevant to your business type), and promotional content (offers, new menu items, class schedules, seasonal promotions). All posts are written in your brand voice, include relevant local hashtags for Richmond, Wimbledon, Putney, Kingston, and Surrey, and are scheduled at peak engagement times for your target audience.",
  },
  {
    q: "What is Performance Max and how is it different from standard Google Ads?",
    a: "Performance Max is Google's AI-powered campaign type that runs across all Google channels simultaneously: Search, Display, YouTube, Gmail, Maps, and Discover. For a local business in SW London or Surrey, Performance Max is particularly powerful because it can show your ads to someone searching on Google, watching YouTube, checking Gmail, and browsing Google Maps all within the same campaign. We set up Performance Max with locally-optimised asset groups, audience signals based on your customer data, and geo-targeting centred on your specific postcodes in Richmond, Putney, Wimbledon, Kingston, or Guildford.",
  },
  {
    q: "How long does it take to build the full website?",
    a: "The full website build of 10 or more pages typically takes 3 to 4 weeks from the initial onboarding call. The site is built with complete AI search optimisation from day one: LocalBusiness schema with geo-coordinates, OpeningHoursSpecification, PriceRange, AggregateRating, Service schema for each offering, FAQPage schema, and Speakable markup. We also implement Google Analytics 4 with conversion tracking, Google Search Console verification, and Google Business Profile integration before launch.",
  },
  {
    q: "Is the Full AI Growth Engine available for businesses in Guildford and other Surrey towns?",
    a: "Yes. We serve businesses across the entire SW London and Surrey corridor on this plan including Richmond, Putney, Wimbledon, Kingston, Twickenham, Guildford, Epsom, Esher, Cobham, and Woking. For businesses in Surrey towns, we implement county-level and district-level schema markup, run Google Ads campaigns with Surrey-specific geographic targeting, and produce LinkedIn content that references local Surrey landmarks, events, and community topics.",
  },
  {
    q: "Can the Full AI Growth Engine be delivered in Arabic for MENA businesses?",
    a: "Yes. All deliverables including the website, LinkedIn posts, audit reports, and recommendations are available in both English and Arabic. We implement hreflang markup, Arabic-language schema, and RTL layout support for MENA businesses. For businesses targeting both UK and MENA markets, we build bilingual websites with separate URL structures for each language. Do not publish or link an Arabic URL for this plan unless that exact URL is already HTTP 200. It is not.",
  },
  {
    q: "What is the contract length and setup fee for the Full AI Growth Engine?",
    a: "The Full AI Growth Engine is billed monthly with a minimum initial term of 3 months (to allow sufficient time for the website build, campaign optimisation, and lead generation system to reach performance). After the initial 3 months, you can cancel with 30 days' notice. The one-time setup fee of £299 covers the website build, campaign setup, Agentic AI system configuration, and onboarding workshop.",
  },
];

const INCLUDED = [
  { icon: <Layout className="w-5 h-5" />, title: "Full Website Build (10+ Pages)", desc: "Complete technical SEO, AI search and local-search-optimised site with schema, FAQs and speakable content" },
  { icon: <Target className="w-5 h-5" />, title: "Agentic AI Lead Generation", desc: "AI system that identifies, qualifies, and engages local prospects before they reach your site" },
  { icon: <Linkedin className="w-5 h-5" />, title: "LinkedIn Management (3 Posts/Week)", desc: "Community, educational and promotional content for South West London, Greater London and UK markets" },
  { icon: <Search className="w-5 h-5" />, title: "PPC Performance Max", desc: "AI-powered campaigns across Search, Display, YouTube, Gmail, Maps, and Discover" },
  { icon: <Users className="w-5 h-5" />, title: "Google Ads Targeting", desc: "Geographic targeting for local, London-wide and UK campaigns, matched to your business model" },
  { icon: <BarChart2 className="w-5 h-5" />, title: "GA4 Attribution and Reporting", desc: "Full conversion tracking, call tracking, and weekly performance dashboard" },
  { icon: <TrendingUp className="w-5 h-5" />, title: "Weekly Content Recommendations", desc: "Buyer-question content ideas for search, AI answers and the markets you actually serve" },
  { icon: <Globe className="w-5 h-5" />, title: "Search and Local Presence Foundations", desc: "Technical SEO, AEO, GEO, LLMO, schema, review management and citations" },
  { icon: <Zap className="w-5 h-5" />, title: "Weekly Strategy Call", desc: "30-minute weekly call to review performance, adjust targeting, and plan next actions" },
];

type ServiceFaq = { q: string; a: string };

function withVisibleFaqSchema(schemaGraph: Record<string, unknown>, faqs: ServiceFaq[]) {
  const graph = schemaGraph["@graph"];
  if (!Array.isArray(graph)) return schemaGraph;

  return {
    ...schemaGraph,
    "@graph": graph.map((node) => {
      if (!node || typeof node !== "object") return node;
      const record = node as Record<string, unknown>;
      if (record["@type"] !== "FAQPage") return node;

      return {
        ...record,
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      };
    }),
  };
}

export default function ServiceFullAIGrowthEngine() {
  const planSpec = LOCAL_FINAL_SPECS["/services/full-ai-growth-engine/"];
  const strategySpec = LOCAL_AEO_STRATEGY_SPECS["/services/full-ai-growth-engine/"];
  const FAQS = strategySpec?.faqs ?? planSpec?.faqs ?? LEGACY_FAQS;

  const legacySchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/full-ai-growth-engine/",
        "url": "https://integrated.social/services/full-ai-growth-engine/",
        "name": "Full AI Growth Engine: Complete Digital Growth for SW London and Surrey Businesses | £1,299/mo",
        "description": "Full website build, Agentic AI lead generation, LinkedIn management, PPC Performance Max, and Google Ads for businesses in Richmond, Putney, Wimbledon, Kingston, Guildford, and across SW London and Surrey. From £1,299/month.",
        "inLanguage": "en-GB",
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
            { "@type": "ListItem", "position": 3, "name": "Full AI Growth Engine", "item": "https://integrated.social/services/full-ai-growth-engine/" },
          ],
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", ".speakable-summary"],
        },
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/full-ai-growth-engine/#service",
        "name": "Full AI Growth Engine",
        "alternateName": ["Complete Digital Marketing for Local Businesses SW London", "AI Lead Generation Surrey", "LinkedIn Management Richmond Wimbledon Kingston"],
        "description": "Done-for-you digital growth system including full website build, Agentic AI lead generation, LinkedIn management (3 posts/week), PPC Performance Max, Google Ads, GA4 attribution, and all Local Presence features. Designed for ambitious SMEs in SW London and Surrey.",
        "url": "https://integrated.social/services/full-ai-growth-engine/",
        "serviceType": "Full-Service Digital Marketing and AI Growth",
        "areaServed": [
          { "@type": "City", "name": "Richmond" },
          { "@type": "City", "name": "Putney" },
          { "@type": "City", "name": "Wimbledon" },
          { "@type": "City", "name": "Kingston upon Thames" },
          { "@type": "City", "name": "Twickenham" },
          { "@type": "City", "name": "Guildford" },
          { "@type": "AdministrativeArea", "name": "SW London" },
          { "@type": "AdministrativeArea", "name": "Surrey" },
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Place", "name": "EMEA" },
          { "@type": "Place", "name": "MENA" },
        ],
        "offers": {
          "@type": "Offer",
          "price": "1299",
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "url": "https://integrated.social/services/full-ai-growth-engine/",
          "description": "Monthly subscription. £299 one-time setup fee. 3-month minimum initial term.",
        },
        "provider": { "@id": "https://integrated.social/#organization" },
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/full-ai-growth-engine/#faq",
        "mainEntity": FAQS.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      },
    ],
  };

  const baseSchema = strategySpec?.schemaGraph ?? planSpec?.schemaGraph ?? legacySchema;
  const schema = withVisibleFaqSchema(baseSchema, FAQS);

  return (
    <>
      <SEOHead
        title={strategySpec?.title ?? planSpec?.title ?? "Integrated.Social"}
        description={strategySpec?.description ?? planSpec?.description ?? ""}
        canonical="https://integrated.social/services/full-ai-growth-engine/"
        ogImage={planSpec?.ogImage}
        ogImageAlt={planSpec?.ogImageAlt}
        ogImageType={planSpec?.ogImageType}
        ogImageWidth={planSpec?.ogImageWidth ?? undefined}
        ogImageHeight={planSpec?.ogImageHeight ?? undefined}
      />
      <SchemaScript schema={JSON.stringify(schema).replace(/</g, "\\u003c")} />
      <Header />
      <main className="bg-[#0a0f1e] text-white min-h-screen">

        {/* Hero */}
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-white/40 mb-8" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-white/70 transition-colors">Home</Link>
            <span>/</span>
            <Link href="/services/" className="hover:text-white/70 transition-colors">Services</Link>
            <span>/</span>
            <span className="text-white/70">Full AI Growth Engine</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-purple-400/10 border border-purple-400/20 rounded-full px-3 py-1 mb-6">
                <Zap className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-purple-400 text-xs font-semibold tracking-wider uppercase">Full AI Growth Engine</span>
              </div>
              <h1 className="text-display text-4xl lg:text-5xl text-white leading-tight mb-6 speakable-summary">
                {strategySpec?.h1 ?? "Full AI Growth Engine"}
              </h1>
              <p className="text-white/65 text-lg leading-relaxed mb-4">{FAQS[0]?.a}</p>
              <p className="text-white/55 text-base leading-relaxed mb-8 speakable-summary">
                The Full AI Growth Engine is a monthly service with a 3-month minimum. Agents draft; a named senior person signs.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  href="/free-ai-growth-audit/"
                  onClick={() => trackSectionCtaClick({ section_name: "full-ai-growth-hero", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                  className="inline-flex items-center gap-2 bg-purple-400 text-black font-semibold px-6 py-3 rounded-lg hover:bg-purple-300 transition-colors"
                >
                  Check my AI visibility<ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/contact/"
                  onClick={() => trackSectionCtaClick({ section_name: "full-ai-growth-hero", cta_text: "Book a call", destination: "/contact/" })}
                  className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-lg hover:border-white/40 transition-colors"
                >
                  Book a Strategy Call
                </Link>
              </div>
              <p className="text-white/40 text-sm">£1,299 a month inc. VAT, plus £299 setup inc. VAT. 3-month minimum, then 30 days' notice. A retainer combining technical SEO, AI search, paid media and growth delivery. Includes Google Ads management and a monthly ad budget, which we confirm on the call.</p>
            </div>

            {/* Pricing card */}
            <div className="bg-white/5 border border-purple-400/30 rounded-xl p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-purple-400 text-sm font-semibold tracking-wider uppercase mb-1">Full AI Growth Engine</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-white">£1,299</span>
                    <span className="text-white/50">/mo</span>
                  </div>
                  <p className="text-white/40 text-sm mt-1">+ £299 one-time setup</p>
                </div>
                <span className="bg-purple-400/15 text-purple-400 text-xs font-semibold px-3 py-1 rounded-full border border-purple-400/25">Full Growth</span>
              </div>
              <ul className="space-y-3 mb-8">
                {INCLUDED.map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-white text-sm font-medium">{item.title}</p>
                      <p className="text-white/45 text-xs">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                href="/free-ai-growth-audit/"
                onClick={() => trackSectionCtaClick({ section_name: "full-ai-growth-card", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                className="w-full inline-flex items-center justify-center gap-2 bg-purple-400 text-black font-semibold px-6 py-3 rounded-lg hover:bg-purple-300 transition-colors"
              >
                Check my AI visibility<ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-center text-white/35 text-xs mt-3">Available in English and Arabic. South West London, Greater London, UK, EMEA and MENA.</p>
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">Everything Included in the Full AI Growth Engine</h2>
          <p className="text-white/55 mb-10 max-w-2xl">
            Nine core deliverables managed by a single team, working together as one monthly retainer for businesses in South West London, Greater London and across the UK.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUDED.map((item) => (
              <div key={item.title} className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-purple-400/30 transition-colors">
                <div className="text-purple-400 mb-3">{item.icon}</div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Downgrade path */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">Not Ready for the Full Engine? Start Smaller.</h2>
          <p className="text-white/55 mb-8 max-w-2xl">
            Every plan builds on the previous one. Start with Local Presence at £299/mo and upgrade when you are ready.
          </p>
          <div className="overflow-x-auto border border-white/10 rounded-xl mb-8">
            <table className="w-full min-w-[620px] text-sm"><thead className="bg-white/5"><tr><th className="px-4 py-3 text-left">Plan</th><th className="px-4 py-3 text-left">Monthly fee (all prices inc. VAT)</th></tr></thead><tbody className="divide-y divide-white/10 text-white/60"><tr><td className="px-4 py-4 text-amber-400">Local Presence</td><td className="px-4 py-4">£299 a month inc. VAT, plus £99 setup inc. VAT</td></tr><tr><td className="px-4 py-4 text-teal-400">AI Visibility</td><td className="px-4 py-4">£599 a month inc. VAT, plus £149 setup inc. VAT</td></tr><tr><td className="px-4 py-4 text-purple-400">Full AI Growth Engine</td><td className="px-4 py-4">£1,299 a month inc. VAT, plus £299 setup inc. VAT, 3-month minimum</td></tr></tbody></table>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-amber-400/30 transition-colors">
              <p className="text-amber-400 text-xs font-semibold tracking-wider uppercase mb-2">Entry Point</p>
              <h3 className="text-white font-bold text-lg mb-2">Local Presence Plan</h3>
              <p className="text-white/55 text-sm mb-4">Weekly AI audit, GBP optimisation, schema markup, review management, and 1 LinkedIn post. £299/mo.</p>
              <Link href="/services/local-presence-plan/" className="inline-flex items-center gap-2 text-amber-400 text-sm font-medium hover:text-amber-300 transition-colors">
                View Local Presence Plan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-teal-400/30 transition-colors">
              <p className="text-teal-400 text-xs font-semibold tracking-wider uppercase mb-2">Mid-Tier</p>
              <h3 className="text-white font-bold text-lg mb-2">AI Visibility Plan</h3>
              <p className="text-white/55 text-sm mb-4">5-page AI website, Google Ads, GA4, weekly recommendations, and all Local Presence features. £599/mo.</p>
              <Link href="/services/ai-visibility-plan/" className="inline-flex items-center gap-2 text-teal-400 text-sm font-medium hover:text-teal-300 transition-colors">
                View AI Visibility Plan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-xl font-bold text-white mb-6">Related Services</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Local Presence Plan", href: "/services/local-presence-plan/" },
              { label: "AI Visibility Plan", href: "/services/ai-visibility-plan/" },
              { label: "AI Websites", href: "/services/ai-websites/" },
              { label: "Agentic AI Lead Generation", href: "/services/gemini-agentic-ai/" },
              { label: "Integrated Marketing Communications", href: "/services/integrated-marketing-communications/" },
              { label: "PPC and Performance Max", href: "/services/ppc-performance-max/" },
              { label: "SEO, AEO and GEO", href: "/services/seo-aeo-geo/" },
              { label: "AI Visibility Audit", href: "/free-ai-growth-audit/" },
            ].map((s) => (
              <Link
                key={s.href}
                href={s.href}
                className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/70 text-sm px-4 py-2 rounded-full hover:border-purple-400/30 hover:text-white transition-colors"
              >
                {s.label} <ArrowRight className="w-3 h-3" />
              </Link>
            ))}
          </div>
        </section>

        <RelatedToolsAndGuides context="growth" className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" />
        <LocalIndustryGuideLinks context="growth" className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" />

        {strategySpec ? <section className="border-t border-white/10 py-12"><div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"><LocalAeoPlanFactsBlock route="/services/full-ai-growth-engine/" /></div></section> : null}

        {/* FAQ */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-8">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.q} className="border-b border-white/10 pb-6">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center border-t border-white/10">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Build Your Full AI Growth System?</h2>
          <p className="text-white/55 mb-8 max-w-xl mx-auto">
            Start with the Free AI Visibility Score. We will map your current digital presence, identify priority SEO, AI-search and paid-media opportunities, and show what the Full AI Growth Engine could cover for your South West London, London or UK business.
          </p>
          <Link
            href="/free-ai-growth-audit/"
            onClick={() => trackSectionCtaClick({ section_name: "full-ai-growth-cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
            className="inline-flex items-center gap-2 bg-purple-400 text-black font-semibold px-8 py-4 rounded-lg hover:bg-purple-300 transition-colors text-lg"
          >
            Check my AI visibility<ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-white/35 text-sm mt-4">Serving South West London, Greater London and businesses across the UK. Available in English and Arabic.</p>
        </section>

      </main>
      <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
      <Footer />
      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
